import { useEffect, useState, useCallback } from 'react';
import { Cookie, ShieldCheck, BarChart3, Megaphone, Settings2, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

/**
 * GDPR / Moldova Legea 195/2024 compliant cookie consent banner.
 *
 * Behaviour:
 * - Shows once on first visit until an explicit choice is stored.
 * - Three primary actions (all same visual weight, per ICO guidance):
 *     * Accept toate  → all categories granted
 *     * Respinge toate → only "necessary" granted (all others denied)
 *     * Personalizează → opens category toggles (opt-in, none pre-checked)
 * - Persists { necessary, preferences, analytics, marketing, version, timestamp }
 *   in localStorage under `cookieConsentV2` and synchronises Google Consent
 *   Mode v2 signals immediately.
 * - Emits a `cookieconsentchange` window event so other scripts (Meta pixel,
 *   TikTok pixel, etc.) can react without reload.
 * - Re-opens itself when the user clicks a "manage cookies" link that fires
 *   the `openCookieSettings` window event (used by the footer link).
 */

const CONSENT_VERSION = 1;
const STORAGE_KEY = 'cookieConsentV2';

const DEFAULT_CHOICES = {
  necessary: true,
  preferences: false,
  analytics: false,
  marketing: false,
};

const TEXTS = {
  ro: {
    title: 'Confidențialitate & cookie-uri',
    intro:
      'Folosim cookie-uri pentru a asigura funcționarea site-ului, pentru a analiza traficul și pentru a personaliza reclamele. Îți poți exprima consimțământul pentru toate sau doar pentru unele categorii. Poți schimba oricând alegerea din subsolul paginii.',
    seeMore: 'Politica de confidențialitate',
    cookiePolicy: 'Politica de cookie-uri',
    acceptAll: 'Permite toate',
    rejectAll: 'Respinge toate',
    customize: 'Personalizează',
    savePrefs: 'Salvează preferințele',
    back: 'Înapoi',
    close: 'Închide',
    categories: {
      necessary: {
        title: 'Strict necesare',
        badge: 'Mereu active',
        desc: 'Cookie-uri esențiale pentru funcționarea site-ului: sesiune, autentificare, coș de cumpărături, protecție anti-spam. Nu pot fi dezactivate.',
      },
      preferences: {
        title: 'Preferințe',
        desc: 'Ne ajută să reținem alegerile tale (limba, valuta, ultimul filtru) pentru a-ți oferi o experiență personalizată la vizita următoare.',
      },
      analytics: {
        title: 'Statistici & analiză',
        desc: 'Google Analytics și alte instrumente ne arată câți vizitatori avem și cum folosesc site-ul, ca să-l putem îmbunătăți. Datele sunt anonimizate.',
      },
      marketing: {
        title: 'Marketing & publicitate',
        desc: 'Permit afișarea de reclame relevante pe alte site-uri (Facebook, Google Ads) și măsoară eficiența campaniilor noastre.',
      },
    },
  },
  ru: {
    title: 'Конфиденциальность и cookie',
    intro:
      'Мы используем cookie-файлы для работы сайта, анализа трафика и персонализации рекламы. Вы можете дать согласие на все категории или только на некоторые. Изменить выбор можно в любой момент из подвала страницы.',
    seeMore: 'Политика конфиденциальности',
    cookiePolicy: 'Политика cookie',
    acceptAll: 'Принять все',
    rejectAll: 'Отклонить все',
    customize: 'Настроить',
    savePrefs: 'Сохранить настройки',
    back: 'Назад',
    close: 'Закрыть',
    categories: {
      necessary: {
        title: 'Строго необходимые',
        badge: 'Всегда активны',
        desc: 'Cookie, без которых сайт не работает: сессия, авторизация, корзина, защита от спама. Отключить нельзя.',
      },
      preferences: {
        title: 'Настройки',
        desc: 'Помогают запомнить ваш выбор (язык, валюта, последний фильтр), чтобы при следующем визите всё было под вас.',
      },
      analytics: {
        title: 'Статистика и аналитика',
        desc: 'Google Analytics и другие инструменты показывают, сколько посетителей у нас и как они используют сайт. Данные анонимизированы.',
      },
      marketing: {
        title: 'Маркетинг и реклама',
        desc: 'Позволяют показывать релевантную рекламу на других сайтах (Facebook, Google Ads) и измерять эффективность кампаний.',
      },
    },
  },
};

const applyConsentToGtag = (choices) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    ad_storage: choices.marketing ? 'granted' : 'denied',
    ad_user_data: choices.marketing ? 'granted' : 'denied',
    ad_personalization: choices.marketing ? 'granted' : 'denied',
    analytics_storage: choices.analytics ? 'granted' : 'denied',
    functionality_storage: choices.preferences ? 'granted' : 'denied',
    personalization_storage: choices.preferences ? 'granted' : 'denied',
  });

  // Lazy-load the GA/GTM script only after analytics consent (compliance
  // with strict GDPR scanners that flag any external tracker requested
  // pre-consent).
  if (choices.analytics && typeof window.__loadGtag === 'function') {
    window.__loadGtag();
  }
};

const persistConsent = (choices) => {
  const record = {
    ...choices,
    necessary: true, // never disable
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch (_) {
    /* storage full or blocked — ignore */
  }
  applyConsentToGtag(record);
  window.dispatchEvent(new CustomEvent('cookieconsentchange', { detail: record }));
};

const CookieBanner = () => {
  const { language } = useLanguage();
  const t = TEXTS[language === 'ru' ? 'ru' : 'ro'];

  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [choices, setChoices] = useState(DEFAULT_CHOICES);

  useEffect(() => {
    let stored = null;
    try {
      stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    } catch (_) {
      stored = null;
    }

    // Show when: no record yet OR record is from an older version
    if (!stored || stored.version !== CONSENT_VERSION) {
      setVisible(true);
    } else {
      setChoices({
        necessary: true,
        preferences: !!stored.preferences,
        analytics: !!stored.analytics,
        marketing: !!stored.marketing,
      });
    }

    const reopen = () => {
      setShowDetails(true);
      setVisible(true);
    };
    window.addEventListener('openCookieSettings', reopen);
    return () => window.removeEventListener('openCookieSettings', reopen);
  }, []);

  const acceptAll = useCallback(() => {
    const next = { necessary: true, preferences: true, analytics: true, marketing: true };
    setChoices(next);
    persistConsent(next);
    setVisible(false);
  }, []);

  const rejectAll = useCallback(() => {
    const next = { ...DEFAULT_CHOICES };
    setChoices(next);
    persistConsent(next);
    setVisible(false);
  }, []);

  const savePreferences = useCallback(() => {
    persistConsent(choices);
    setVisible(false);
  }, [choices]);

  if (!visible) return null;

  return (
    <>
      {/* Semi-transparent backdrop makes clear the visitor must act (no default consent) */}
      <div
        className="fixed inset-0 z-[99998] bg-black/30 backdrop-blur-[2px]"
        aria-hidden="true"
        data-testid="cookie-banner-backdrop"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-banner-title"
        className="fixed inset-x-3 bottom-3 z-[99999] md:inset-x-auto md:bottom-6 md:right-6 md:max-w-[560px]"
        data-testid="cookie-banner"
      >
        <div className="rounded-2xl bg-white p-5 shadow-2xl ring-1 ring-black/10 md:p-6">
          <div className="mb-3 flex items-start gap-3">
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#a7cf26]/12 text-[#5a7a10]">
              <Cookie className="h-6 w-6" />
            </div>

            <div className="flex-1">
              <h3
                id="cookie-banner-title"
                className="text-lg font-black text-gray-900"
              >
                {t.title}
              </h3>
            </div>
          </div>

          {!showDetails && (
            <>
              <p className="text-sm leading-relaxed text-gray-600">{t.intro}</p>

              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm font-semibold text-[#5a7a10]">
                <a
                  href="/page/politica-de-confidentialitate"
                  className="hover:underline"
                  data-testid="cookie-banner-policy-link"
                >
                  {t.seeMore} →
                </a>
                <a
                  href="/page/politica-cookie"
                  className="hover:underline"
                  data-testid="cookie-banner-cookie-policy-link"
                >
                  {t.cookiePolicy} →
                </a>
              </div>

              <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-3">
                <button
                  type="button"
                  onClick={rejectAll}
                  className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-bold text-gray-800 transition hover:bg-gray-50"
                  data-testid="cookie-banner-reject-all"
                >
                  {t.rejectAll}
                </button>

                <button
                  type="button"
                  onClick={() => setShowDetails(true)}
                  className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-bold text-gray-800 transition hover:bg-gray-50"
                  data-testid="cookie-banner-customize"
                >
                  {t.customize}
                </button>

                <button
                  type="button"
                  onClick={acceptAll}
                  className="rounded-xl bg-[#a7cf26] px-4 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-[#96bc21]"
                  data-testid="cookie-banner-accept-all"
                >
                  {t.acceptAll}
                </button>
              </div>
            </>
          )}

          {showDetails && (
            <div className="max-h-[65vh] overflow-y-auto pr-1">
              <p className="mb-4 text-sm leading-relaxed text-gray-600">{t.intro}</p>

              <div className="space-y-3">
                <CategoryRow
                  icon={<ShieldCheck className="h-5 w-5" />}
                  title={t.categories.necessary.title}
                  desc={t.categories.necessary.desc}
                  checked
                  disabled
                  badge={t.categories.necessary.badge}
                  testId="cookie-category-necessary"
                />
                <CategoryRow
                  icon={<Settings2 className="h-5 w-5" />}
                  title={t.categories.preferences.title}
                  desc={t.categories.preferences.desc}
                  checked={choices.preferences}
                  onChange={(v) => setChoices((c) => ({ ...c, preferences: v }))}
                  testId="cookie-category-preferences"
                />
                <CategoryRow
                  icon={<BarChart3 className="h-5 w-5" />}
                  title={t.categories.analytics.title}
                  desc={t.categories.analytics.desc}
                  checked={choices.analytics}
                  onChange={(v) => setChoices((c) => ({ ...c, analytics: v }))}
                  testId="cookie-category-analytics"
                />
                <CategoryRow
                  icon={<Megaphone className="h-5 w-5" />}
                  title={t.categories.marketing.title}
                  desc={t.categories.marketing.desc}
                  checked={choices.marketing}
                  onChange={(v) => setChoices((c) => ({ ...c, marketing: v }))}
                  testId="cookie-category-marketing"
                />
              </div>

              <div className="mt-5 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-4">
                <button
                  type="button"
                  onClick={() => setShowDetails(false)}
                  className="text-sm font-bold text-gray-600 hover:text-gray-900"
                  data-testid="cookie-banner-back"
                >
                  ← {t.back}
                </button>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={rejectAll}
                    className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-bold text-gray-800 transition hover:bg-gray-50"
                    data-testid="cookie-banner-details-reject-all"
                  >
                    {t.rejectAll}
                  </button>
                  <button
                    type="button"
                    onClick={savePreferences}
                    className="rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-gray-800"
                    data-testid="cookie-banner-save-preferences"
                  >
                    {t.savePrefs}
                  </button>
                  <button
                    type="button"
                    onClick={acceptAll}
                    className="rounded-xl bg-[#a7cf26] px-4 py-2.5 text-sm font-black text-white transition hover:bg-[#96bc21]"
                    data-testid="cookie-banner-details-accept-all"
                  >
                    {t.acceptAll}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const CategoryRow = ({ icon, title, desc, checked, onChange, disabled, badge, testId }) => (
  <div
    className="rounded-xl border border-gray-200 bg-gray-50/60 p-4"
    data-testid={testId}
  >
    <div className="flex items-start gap-3">
      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-white text-[#5a7a10] ring-1 ring-black/5">
        {icon}
      </div>

      <div className="flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-black text-gray-900">{title}</span>
          {badge && (
            <span className="rounded-full bg-[#a7cf26]/20 px-2 py-0.5 text-[11px] font-bold text-[#5a7a10]">
              {badge}
            </span>
          )}
        </div>
        <p className="mt-1 text-sm leading-relaxed text-gray-600">{desc}</p>
      </div>

      <label
        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition ${
          checked ? 'bg-[#a7cf26]' : 'bg-gray-300'
        } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
      >
        <input
          type="checkbox"
          className="peer sr-only"
          checked={!!checked}
          disabled={disabled}
          onChange={(e) => !disabled && onChange && onChange(e.target.checked)}
          data-testid={`${testId}-toggle`}
        />
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
            checked ? 'translate-x-5' : 'translate-x-0.5'
          }`}
        />
      </label>
    </div>
  </div>
);

export default CookieBanner;
